import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { X, Package, Search, Plus, Trash2, Loader2, ShoppingBag, DollarSign, Hash, FileText, AlertCircle } from 'lucide-react';

function ComboModal({ userId, onClose, onComboCreated }) {
  const [step, setStep] = useState('list'); // 'list' or 'create'
  const [combos, setCombos] = useState([]);
  const [loadingCombos, setLoadingCombos] = useState(false);
  
  // Product search state
  const [products, setProducts] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [categories, setCategories] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);
  
  // Combo form state
  const [comboName, setComboName] = useState('');
  const [comboDescription, setComboDescription] = useState('');
  const [comboSku, setComboSku] = useState('');
  const [comboPrice, setComboPrice] = useState('');
  const [selectedProducts, setSelectedProducts] = useState([]); // [{ product, quantity }]
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  
  const observer = useRef();
  const lastProductRef = useCallback(node => {
    if (loadingProducts) return;
    if (observer.current) observer.current.disconnect();
    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore) {
        setPage(prevPage => prevPage + 1);
      }
    });
    if (node) observer.current.observe(node);
  }, [loadingProducts, hasMore]);

  const baseUrl = process.env.REACT_APP_API_URL || '';

  useEffect(() => {
    if (step === 'list') {
      fetchCombos();
    }
  }, [step]);

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    if (step === 'create') {
      if (initialLoad) {
        setInitialLoad(false);
        fetchProducts(1, true);
      } else {
        const timer = setTimeout(() => {
          setPage(1);
          fetchProducts(1, true);
        }, 500);
        return () => clearTimeout(timer);
      }
    }
  }, [searchQuery, selectedCategory, step]);

  useEffect(() => {
    if (step === 'create' && page > 1) {
      fetchProducts(page, false);
    }
  }, [page]);

  async function fetchCombos() {
    setLoadingCombos(true);
    try {
      const { data } = await axios.get(`${baseUrl}/api/combos`, {
        params: { user_id: userId }
      });
      setCombos(data.combos);
    } catch (error) {
      console.error('Error fetching combos:', error);
    } finally {
      setLoadingCombos(false);
    }
  }

  async function fetchCategories() {
    try {
      const { data } = await axios.get(`${baseUrl}/api/categories`);
      setCategories(data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  }

  async function fetchProducts(pageNum, reset = false) {
    setLoadingProducts(true);
    try {
      const { data } = await axios.get(`${baseUrl}/api/products/search`, {
        params: {
          user_id: userId,
          search: searchQuery,
          category_id: selectedCategory,
          page: pageNum,
          per_page: 20
        }
      });

      if (reset) {
        setProducts(data.products);
      } else {
        setProducts(prev => [...prev, ...data.products]);
      }
      
      setHasMore(data.has_next);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoadingProducts(false);
    }
  }

  function addProductToCombo(product) {
    // Check if product already added
    if (selectedProducts.some(item => item.product.id === product.id)) {
      return;
    }
    
    setSelectedProducts([...selectedProducts, { product, quantity: 1 }]);
  }

  function removeProductFromCombo(productId) {
    setSelectedProducts(selectedProducts.filter(item => item.product.id !== productId));
  }

  function updateProductQuantity(productId, quantity) {
    const qty = parseInt(quantity);
    if (isNaN(qty) || qty < 1) return;
    
    setSelectedProducts(selectedProducts.map(item =>
      item.product.id === productId ? { ...item, quantity: qty } : item
    ));
  }

  function calculateTotalRegularPrice() {
    return selectedProducts.reduce((sum, item) => {
      return sum + (item.product.price * item.quantity);
    }, 0);
  }

  function calculateSavings() {
    const regularPrice = calculateTotalRegularPrice();
    const comboP = parseFloat(comboPrice) || 0;
    return regularPrice - comboP;
  }

  async function handleCreateCombo() {
    // Validation
    if (!comboName.trim()) {
      setError('El nombre del combo es obligatorio');
      return;
    }
    
    if (!comboSku.trim()) {
      setError('El SKU del combo es obligatorio');
      return;
    }
    
    if (!comboPrice || parseFloat(comboPrice) <= 0) {
      setError('El precio del combo debe ser mayor a 0');
      return;
    }
    
    if (selectedProducts.length === 0) {
      setError('Debes agregar al menos un producto al combo');
      return;
    }

    setSaving(true);
    setError('');

    try {
      const payload = {
        user_id: userId,
        name: comboName.trim(),
        description: comboDescription.trim(),
        sku: comboSku.trim(),
        combo_price: parseFloat(comboPrice),
        items: selectedProducts.map(item => ({
          product_id: item.product.id,
          quantity: item.quantity
        }))
      };

      const { data } = await axios.post(`${baseUrl}/api/combos`, payload);
      
      // Reset form
      setComboName('');
      setComboDescription('');
      setComboSku('');
      setComboPrice('');
      setSelectedProducts([]);
      setStep('list');
      
      if (onComboCreated) {
        onComboCreated(data);
      }
    } catch (error) {
      console.error('Error creating combo:', error);
      setError(error.response?.data?.error || 'Error al crear el combo');
    } finally {
      setSaving(false);
    }
  }

  async function handleDeleteCombo(comboId) {
    if (!window.confirm('¿Estás seguro de eliminar este combo?')) {
      return;
    }

    try {
      await axios.delete(`${baseUrl}/api/combos/${comboId}`);
      setCombos(combos.filter(c => c.id !== comboId));
    } catch (error) {
      console.error('Error deleting combo:', error);
      alert(error.response?.data?.error || 'Error al eliminar el combo');
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-6xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-slate-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-brand-primary/10 rounded-xl flex items-center justify-center">
              <ShoppingBag className="w-5 h-5 text-brand-primary" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-900 font-montserrat">
                {step === 'list' ? 'Mis Combos' : 'Crear Nuevo Combo'}
              </h2>
              <p className="text-sm text-slate-600 font-montserrat">
                {step === 'list' 
                  ? 'Gestiona tus combos de productos'
                  : 'Selecciona productos y configura tu combo'
                }
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {step === 'list' ? (
            <>
              {/* Create Button */}
              <button
                onClick={() => setStep('create')}
                className="w-full mb-6 px-4 py-3 bg-brand-primary text-white rounded-xl font-semibold font-montserrat hover:bg-brand-primary/90 transition-colors flex items-center justify-center gap-2"
              >
                <Plus className="w-5 h-5" />
                Crear Nuevo Combo
              </button>

              {/* Combos List */}
              {loadingCombos ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 text-brand-primary animate-spin" />
                </div>
              ) : combos.length === 0 ? (
                <div className="text-center py-12">
                  <ShoppingBag className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-bold text-slate-900 font-montserrat mb-2">
                    No tienes combos creados
                  </h3>
                  <p className="text-sm text-slate-600 font-montserrat">
                    Crea tu primer combo para empezar
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {combos.map(combo => (
                    <div key={combo.id} className="bg-white border border-slate-200 rounded-xl p-4 hover:shadow-md transition-shadow">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <h3 className="text-base font-bold text-slate-900 font-montserrat mb-1">
                            {combo.name}
                          </h3>
                          <p className="text-xs text-slate-500 font-montserrat">
                            SKU: {combo.sku}
                          </p>
                        </div>
                        <button
                          onClick={() => handleDeleteCombo(combo.id)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>

                      {combo.description && (
                        <p className="text-sm text-slate-600 font-montserrat mb-3 line-clamp-2">
                          {combo.description}
                        </p>
                      )}

                      <div className="flex items-center gap-4 mb-3">
                        <div>
                          <p className="text-xs text-slate-500 font-montserrat">Precio Combo</p>
                          <p className="text-lg font-bold text-brand-primary font-montserrat">
                            S/ {combo.combo_price.toFixed(2)}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-500 font-montserrat">Productos</p>
                          <p className="text-lg font-bold text-slate-900 font-montserrat">
                            {combo.total_items}
                          </p>
                        </div>
                      </div>

                      {/* Product Images Preview */}
                      {combo.item_images && combo.item_images.length > 0 && (
                        <div className="flex items-center gap-2 overflow-x-auto">
                          {combo.item_images.slice(0, 4).map((img, idx) => (
                            <div key={idx} className="relative flex-shrink-0">
                              {img.image_url ? (
                                <img
                                  src={img.image_url}
                                  alt={img.product_title}
                                  className="w-12 h-12 object-cover rounded-lg border border-slate-200"
                                />
                              ) : (
                                <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center">
                                  <Package className="w-6 h-6 text-slate-400" />
                                </div>
                              )}
                              {img.quantity > 1 && (
                                <span className="absolute -top-1 -right-1 bg-brand-primary text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                                  {img.quantity}
                                </span>
                              )}
                            </div>
                          ))}
                          {combo.item_images.length > 4 && (
                            <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center">
                              <span className="text-xs font-bold text-slate-600">
                                +{combo.item_images.length - 4}
                              </span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Left: Product Selection */}
              <div className="space-y-4">
                <h3 className="text-base font-bold text-slate-900 font-montserrat">
                  Seleccionar Productos
                </h3>

                {/* Search and Filter */}
                <div className="space-y-3">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <input
                      type="text"
                      placeholder="Buscar por nombre o SKU..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full pl-10 pr-4 py-2.5 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary text-sm font-montserrat"
                    />
                  </div>

                  <select
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    className="w-full px-4 py-2.5 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary text-sm font-montserrat bg-white"
                  >
                    <option value="">Todas las categorías</option>
                    {categories.map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                  </select>
                </div>

                {/* Products List */}
                <div className="border border-slate-200 rounded-xl max-h-96 overflow-y-auto">
                  {products.length === 0 && !loadingProducts ? (
                    <div className="p-8 text-center">
                      <Package className="w-12 h-12 text-slate-300 mx-auto mb-2" />
                      <p className="text-sm text-slate-600 font-montserrat">
                        No se encontraron productos
                      </p>
                    </div>
                  ) : (
                    <div className="divide-y divide-slate-200">
                      {products.map((product, index) => {
                        const isSelected = selectedProducts.some(item => item.product.id === product.id);
                        const isLast = index === products.length - 1;

                        return (
                          <div
                            key={product.id}
                            ref={isLast ? lastProductRef : null}
                            className={`p-3 hover:bg-slate-50 transition-colors ${
                              isSelected ? 'bg-brand-primary/5' : ''
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              {product.primary_image ? (
                                <img
                                  src={product.primary_image}
                                  alt={product.title}
                                  className="w-12 h-12 object-cover rounded-lg border border-slate-200"
                                />
                              ) : (
                                <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center">
                                  <Package className="w-6 h-6 text-slate-400" />
                                </div>
                              )}

                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-semibold text-slate-900 font-montserrat truncate">
                                  {product.title}
                                </p>
                                <p className="text-xs text-slate-500 font-montserrat">
                                  {product.sku} • S/ {product.price.toFixed(2)}
                                </p>
                              </div>

                              <button
                                onClick={() => addProductToCombo(product)}
                                disabled={isSelected}
                                className={`p-2 rounded-lg transition-colors ${
                                  isSelected
                                    ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                                    : 'bg-brand-primary text-white hover:bg-brand-primary/90'
                                }`}
                              >
                                <Plus className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {loadingProducts && (
                    <div className="p-4 text-center">
                      <Loader2 className="w-6 h-6 text-brand-primary animate-spin mx-auto" />
                    </div>
                  )}
                </div>
              </div>

              {/* Right: Combo Configuration */}
              <div className="space-y-4">
                <h3 className="text-base font-bold text-slate-900 font-montserrat">
                  Configurar Combo
                </h3>

                {/* Combo Details Form */}
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 font-montserrat mb-1">
                      Nombre del Combo *
                    </label>
                    <input
                      type="text"
                      value={comboName}
                      onChange={(e) => setComboName(e.target.value)}
                      placeholder="Ej: Pack Verano 2024"
                      className="w-full px-4 py-2.5 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary text-sm font-montserrat"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-slate-700 font-montserrat mb-1">
                      SKU del Combo *
                    </label>
                    <div className="relative">
                      <Hash className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                      <input
                        type="text"
                        value={comboSku}
                        onChange={(e) => setComboSku(e.target.value)}
                        placeholder="COMBO-001"
                        className="w-full pl-10 pr-4 py-2.5 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary text-sm font-montserrat"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-slate-700 font-montserrat mb-1">
                      Precio del Combo *
                    </label>
                    <div className="relative">
                      <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                      <input
                        type="number"
                        step="0.01"
                        value={comboPrice}
                        onChange={(e) => setComboPrice(e.target.value)}
                        placeholder="0.00"
                        className="w-full pl-10 pr-4 py-2.5 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary text-sm font-montserrat"
                      />
                    </div>
                    {comboPrice && selectedProducts.length > 0 && (
                      <div className="mt-2 text-xs font-montserrat">
                        <p className="text-slate-600">
                          Precio regular: S/ {calculateTotalRegularPrice().toFixed(2)}
                        </p>
                        {calculateSavings() > 0 && (
                          <p className="text-brand-green font-semibold">
                            Ahorro: S/ {calculateSavings().toFixed(2)}
                          </p>
                        )}
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-slate-700 font-montserrat mb-1">
                      Descripción
                    </label>
                    <textarea
                      value={comboDescription}
                      onChange={(e) => setComboDescription(e.target.value)}
                      placeholder="Describe tu combo..."
                      rows="3"
                      className="w-full px-4 py-2.5 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary text-sm font-montserrat resize-none"
                    />
                  </div>
                </div>

                {/* Selected Products */}
                <div>
                  <label className="block text-sm font-semibold text-slate-700 font-montserrat mb-2">
                    Productos Seleccionados ({selectedProducts.length})
                  </label>
                  
                  {selectedProducts.length === 0 ? (
                    <div className="border-2 border-dashed border-slate-200 rounded-xl p-6 text-center">
                      <Package className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                      <p className="text-sm text-slate-500 font-montserrat">
                        Selecciona productos de la lista
                      </p>
                    </div>
                  ) : (
                    <div className="border border-slate-200 rounded-xl divide-y divide-slate-200 max-h-64 overflow-y-auto">
                      {selectedProducts.map(item => (
                        <div key={item.product.id} className="p-3">
                          <div className="flex items-center gap-3">
                            {item.product.primary_image ? (
                              <img
                                src={item.product.primary_image}
                                alt={item.product.title}
                                className="w-10 h-10 object-cover rounded-lg border border-slate-200"
                              />
                            ) : (
                              <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                                <Package className="w-5 h-5 text-slate-400" />
                              </div>
                            )}

                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-semibold text-slate-900 font-montserrat truncate">
                                {item.product.title}
                              </p>
                              <p className="text-xs text-slate-500 font-montserrat">
                                S/ {item.product.price.toFixed(2)} c/u
                              </p>
                            </div>

                            <input
                              type="number"
                              min="1"
                              value={item.quantity}
                              onChange={(e) => updateProductQuantity(item.product.id, e.target.value)}
                              className="w-16 px-2 py-1 border border-slate-300 rounded-lg text-center text-sm font-montserrat focus:outline-none focus:ring-2 focus:ring-brand-primary/20"
                            />

                            <button
                              onClick={() => removeProductFromCombo(item.product.id)}
                              className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Error Message */}
                {error && (
                  <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-xl">
                    <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
                    <p className="text-sm text-red-600 font-montserrat">{error}</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 p-6 border-t border-slate-200">
          {step === 'create' ? (
            <>
              <button
                onClick={() => {
                  setStep('list');
                  setSelectedProducts([]);
                  setComboName('');
                  setComboDescription('');
                  setComboSku('');
                  setComboPrice('');
                  setError('');
                }}
                disabled={saving}
                className="px-4 py-2.5 border border-slate-300 text-slate-700 rounded-xl font-semibold font-montserrat hover:bg-slate-50 transition-colors disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                onClick={handleCreateCombo}
                disabled={saving || selectedProducts.length === 0}
                className="px-6 py-2.5 bg-brand-primary text-white rounded-xl font-semibold font-montserrat hover:bg-brand-primary/90 transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Guardando...
                  </>
                ) : (
                  <>
                    <Plus className="w-4 h-4" />
                    Crear Combo
                  </>
                )}
              </button>
            </>
          ) : (
            <button
              onClick={onClose}
              className="ml-auto px-6 py-2.5 bg-slate-200 text-slate-700 rounded-xl font-semibold font-montserrat hover:bg-slate-300 transition-colors"
            >
              Cerrar
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default ComboModal;